// contexts/ParametresContext.jsx
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { api } from '../services/api';

const ParametresContext = createContext();

export const useParametres = () => {
  const context = useContext(ParametresContext);
  if (!context) {
    throw new Error('useParametres must be used within a ParametresProvider');
  }
  return context;
};

// ============================================================================
// NOUVELLES FONCTIONS UTILITAIRES POUR L'AUTHENTIFICATION
// ============================================================================
const checkToken = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    console.error('?? [CONTEXT] Aucun token trouvé');
    return false;
  }
  
  // Validation basique du format JWT
  const parts = token.split('.');
  if (parts.length !== 3) {
    console.error('?? [CONTEXT] Token invalide (format)');
    localStorage.removeItem('token');
    return false;
  }
  
  // Vérifier l'expiration
  try {
    const payload = JSON.parse(atob(parts[1]));
    if (payload.exp && payload.exp < Date.now() / 1000) {
      console.error('?? [CONTEXT] Token expiré');
      localStorage.removeItem('token');
      return false;
    }
  } catch (e) {
    console.error('?? [CONTEXT] Erreur décodage token');
    localStorage.removeItem('token');
    return false;
  }
  
  return true;
};

const handleAuthError = () => {
  console.log('?? [CONTEXT] Gestion erreur d\'authentification');
  
  // Nettoyer le localStorage
  localStorage.removeItem('token');
  
  // Optionnel: rediriger vers la page de connexion
  // window.location.href = '/login';
  
  // Ou afficher une notification à l'utilisateur
  console.log('Session expirée - redirection nécessaire');
};

export const ParametresProvider = ({ children }) => {
  const [parametres, setParametres] = useState({
    parametres_generaux: {
      seuil_eleves_defaut: 25,
      monnaie_code: 'FCFA',
      monnaie_symbole: 'FCFA',
      nom_ecole: 'Mon École'
    },
    sections: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ============================================================================
  // PROTECTION ANTI-RACE RENFORCÉE
  // ============================================================================
  const reqIdRef = useRef(0);
  const abortRef = useRef(null);
  const isLoadingRef = useRef(false);
  const lastUpdateRef = useRef(0);
  
  // Debouncing pour éviter appels multiples
  const debouncedCallsRef = useRef({
    updateSection: null,
    updateGeneraux: null,
    createSection: null,
    reorderSections: null
  });

  // ============================================================================
  // NORMALISATION AVEC LOGS DÉTAILLÉS
  // ============================================================================
  const normalize = (resp) => {
    const normalizeId = Math.random().toString(36).substr(2, 6);
    console.log(`?? [NORMALIZE-${normalizeId}] ===== DÉBUT NORMALISATION =====`);
    console.log(`?? [NORMALIZE-${normalizeId}] Réponse brute:`, JSON.stringify(resp, null, 2));
    
    // Paramètres généraux
    const rawPG = resp?.data?.parametres_generaux;
    console.log(`?? [NORMALIZE-${normalizeId}] Paramètres généraux bruts:`, rawPG);
    
    const pg = rawPG ?? {
      seuil_eleves_defaut: 25,
      monnaie_code: 'FCFA',
      monnaie_symbole: 'FCFA',
      nom_ecole: 'Mon École'
    };
    console.log(`?? [NORMALIZE-${normalizeId}] Paramètres généraux normalisés:`, pg);
    
    // Sections avec logs ultra-détaillés
    const rawSections = resp?.data?.sections;
    console.log(`?? [NORMALIZE-${normalizeId}] Sections brutes:`, rawSections);
    console.log(`?? [NORMALIZE-${normalizeId}] Type sections:`, typeof rawSections);
    console.log(`?? [NORMALIZE-${normalizeId}] Est tableau:`, Array.isArray(rawSections));
    console.log(`?? [NORMALIZE-${normalizeId}] Longueur:`, rawSections?.length);
    
    const sections = (rawSections ?? []).map((s, index) => {
      const activeRaw = s.active;
      const activeNumber = Number(s.active);
      const activeBoolean = activeNumber === 1;
      
      console.log(`?? [NORMALIZE-${normalizeId}] Section ${index} "${s.nom}":`, {
        id: s.id,
        nom: s.nom,
        active_raw: activeRaw,
        active_raw_type: typeof activeRaw,
        active_number: activeNumber,
        active_boolean: activeBoolean,
        transformation: `"${activeRaw}" (${typeof activeRaw}) ? ${activeNumber} ? ${activeBoolean}`,
        toutes_proprietes: Object.keys(s)
      });
      
      return {
        ...s,
        active: activeBoolean
      };
    });
    
    console.log(`?? [NORMALIZE-${normalizeId}] Sections finales:`, 
      sections.map(s => ({ id: s.id, nom: s.nom, active: s.active, type: typeof s.active }))
    );
    
    const result = { parametres_generaux: pg, sections };
    console.log(`?? [NORMALIZE-${normalizeId}] ===== FIN NORMALISATION =====`);
    console.log(`?? [NORMALIZE-${normalizeId}] Résultat final:`, result);
    
    return result;
  };

  // ============================================================================
  // FONCTION PRINCIPALE LOAD PARAMETRES - VERSION MISE À JOUR
  // ============================================================================
  const loadParametres = async (force = false, caller = 'unknown') => {
    const callTime = Date.now();
    
    // ? VÉRIFICATION TOKEN EN PREMIER
    if (!checkToken()) {
      console.error('?? [CONTEXT] Token invalide, arrêt du chargement');
      setError('Authentification requise - Veuillez vous reconnecter');
      setLoading(false);
      return parametres;
    }
    
    // ? PROTECTION ANTI-SPAM ultra-stricte
    if (isLoadingRef.current && !force) {
      const timeSinceLastUpdate = callTime - lastUpdateRef.current;
      console.log(`?? [CONTEXT] loadParametres SKIP - déjà en cours`);
      console.log(`?? [CONTEXT] Caller: ${caller}, Force: ${force}, Time since last: ${timeSinceLastUpdate}ms`);
      return parametres;
    }
    
    const myId = ++reqIdRef.current;
    const startTime = performance.now();
    lastUpdateRef.current = callTime;
    
    console.log(`?? [CONTEXT-${myId}] ======================================`);
    console.log(`?? [CONTEXT-${myId}] DÉBUT LOAD PARAMETRES`);
    console.log(`?? [CONTEXT-${myId}] Caller: ${caller}`);
    console.log(`?? [CONTEXT-${myId}] Force: ${force}`);
    console.log(`?? [CONTEXT-${myId}] Timestamp: ${new Date().toISOString()}`);
    console.log(`?? [CONTEXT-${myId}] ID requête: ${myId}`);
    console.log(`?? [CONTEXT-${myId}] État loading actuel: ${loading}`);
    console.log(`?? [CONTEXT-${myId}] isLoadingRef: ${isLoadingRef.current}`);
    console.log(`?? [CONTEXT-${myId}] Sections actuelles (${parametres.sections.length}):`, 
      parametres.sections.map(s => ({ id: s.id, nom: s.nom, active: s.active }))
    );
    
    // ? ABORT propre des requêtes précédentes
    if (abortRef.current) {
      console.log(`?? [CONTEXT-${myId}] Abort requête précédente`);
      abortRef.current.abort();
    }
    
    const controller = new AbortController();
    abortRef.current = controller;
    isLoadingRef.current = true;

    try {
      console.log(`? [CONTEXT-${myId}] setLoading(true) + setError(null)`);
      setLoading(true);
      setError(null);
      
      console.log(`?? [CONTEXT-${myId}] Appel API parametres.getAll()...`);
      const response = await api.parametres.getAll({ 
        signal: controller.signal,
        _caller: `context-${myId}-${caller}`
      });
      
      const apiTime = performance.now() - startTime;
      console.log(`? [CONTEXT-${myId}] API terminé en ${apiTime.toFixed(2)}ms`);
      console.log(`?? [CONTEXT-${myId}] Réponse API complète:`, response);
      
      // ? VÉRIFICATIONS RACE CONDITION strictes
      if (myId !== reqIdRef.current) {
        console.log(`?? [CONTEXT-${myId}] RACE CONDITION détectée !`);
        console.log(`?? [CONTEXT-${myId}] myId: ${myId}, current: ${reqIdRef.current}`);
        return parametres;
      }
      
      if (controller.signal.aborted) {
        console.log(`?? [CONTEXT-${myId}] Signal ABORTED !`);
        return parametres;
      }
      
      console.log(`? [CONTEXT-${myId}] Pas de race condition, traitement...`);

      if (response && response.success) {
        console.log(`? [CONTEXT-${myId}] Réponse SUCCESS, normalisation...`);
        
        const normalized = normalize(response);
        
        // ? COMPARAISON DÉTAILLÉE avant/après
        console.log(`?? [CONTEXT-${myId}] ===== COMPARAISON AVANT/APRÈS =====`);
        console.log(`?? [CONTEXT-${myId}] AVANT - Sections (${parametres.sections.length}):`, 
          parametres.sections.map(s => ({ id: s.id, nom: s.nom, active: s.active, type: typeof s.active }))
        );
        console.log(`?? [CONTEXT-${myId}] APRÈS - Sections (${normalized.sections.length}):`, 
          normalized.sections.map(s => ({ id: s.id, nom: s.nom, active: s.active, type: typeof s.active }))
        );
        
        // ? DÉTECTION DES CHANGEMENTS
        const changes = [];
        normalized.sections.forEach(newSection => {
          const oldSection = parametres.sections.find(old => old.id === newSection.id);
          if (oldSection) {
            if (oldSection.active !== newSection.active) {
              changes.push({
                type: 'active_changed',
                id: newSection.id,
                nom: newSection.nom,
                ancien: oldSection.active,
                nouveau: newSection.active
              });
            }
            if (oldSection.nom !== newSection.nom) {
              changes.push({
                type: 'nom_changed',
                id: newSection.id,
                ancien: oldSection.nom,
                nouveau: newSection.nom
              });
            }
          } else {
            changes.push({
              type: 'section_added',
              id: newSection.id,
              nom: newSection.nom,
              active: newSection.active
            });
          }
        });
        
        parametres.sections.forEach(oldSection => {
          if (!normalized.sections.find(ns => ns.id === oldSection.id)) {
            changes.push({
              type: 'section_removed',
              id: oldSection.id,
              nom: oldSection.nom
            });
          }
        });
        
        if (changes.length > 0) {
          console.log(`?? [CONTEXT-${myId}] CHANGEMENTS DÉTECTÉS (${changes.length}):`, changes);
        } else {
          console.log(`? [CONTEXT-${myId}] Aucun changement détecté`);
        }
        
        console.log(`?? [CONTEXT-${myId}] Application setParametres...`);
        setParametres(normalized);
        console.log(`? [CONTEXT-${myId}] setParametres APPLIQUÉ avec succès`);
        
        return normalized;
        
      } else if (response?.unauthorized) {
        console.log(`?? [CONTEXT-${myId}] ERREUR: Non authentifié`);
        setError('Session expirée - Veuillez vous reconnecter');
        localStorage.removeItem('token'); // Nettoyer le token invalide
        setParametres(prev => ({ ...prev, sections: [] }));
        return parametres;
      } else if (response?.forbidden) {
        console.log(`?? [CONTEXT-${myId}] ERREUR: Accès interdit`);
        setError('Accès interdit - Droits insuffisants');
        setParametres(prev => ({ ...prev, sections: [] }));
        return parametres;
      } else {
        console.log(`? [CONTEXT-${myId}] ERREUR: Réponse non réussie`);
        console.log(`? [CONTEXT-${myId}] Response détail:`, response);
        
        // ? GESTION SPÉCIALE DES ERREURS D'AUTHENTIFICATION
        if (response?.error && response.error.includes('Authentification')) {
          console.log(`?? [CONTEXT-${myId}] Erreur d'authentification détectée`);
          setError('Session expirée - Veuillez vous reconnecter');
          localStorage.removeItem('token');
          handleAuthError();
        } else {
          setError('Erreur lors du chargement des paramètres');
        }
        return parametres;
      }
      
    } catch (error) {
      const errorTime = performance.now() - startTime;
      
      if (error.name === 'AbortError') {
        console.log(`?? [CONTEXT-${myId}] Requête ANNULÉE (AbortError)`);
      } else {
        console.log(`?? [CONTEXT-${myId}] ===== ERREUR CATCH =====`);
        console.log(`?? [CONTEXT-${myId}] Error.name: ${error.name}`);
        console.log(`?? [CONTEXT-${myId}] Error.message: ${error.message}`);
        console.log(`?? [CONTEXT-${myId}] Error.stack:`, error.stack);
        console.log(`?? [CONTEXT-${myId}] Temps écoulé: ${errorTime.toFixed(2)}ms`);
        console.log(`?? [CONTEXT-${myId}] Caller: ${caller}`);
        console.error(`?? [CONTEXT-${myId}] Erreur complète:`, error);
        
        // ? GESTION SPÉCIALE DES ERREURS D'AUTHENTIFICATION
        if (error.message.includes('Authentification') || 
            error.message.includes('401') || 
            error.message.includes('Unauthorized')) {
          console.log(`?? [CONTEXT-${myId}] Erreur 401 détectée dans le catch`);
          setError('Session expirée - Veuillez vous reconnecter');
          localStorage.removeItem('token');
          handleAuthError();
        } else {
          setError(error.message);
        }
      }
      return parametres;
    } finally {
      const totalTime = performance.now() - startTime;
      
      if (myId === reqIdRef.current && !controller.signal.aborted) {
        console.log(`?? [CONTEXT-${myId}] setLoading(false)`);
        console.log(`?? [CONTEXT-${myId}] isLoadingRef = false`);
        isLoadingRef.current = false;
        setLoading(false);
      } else {
        console.log(`?? [CONTEXT-${myId}] SKIP setLoading(false) - obsolète ou aborted`);
      }
      
      console.log(`?? [CONTEXT-${myId}] TEMPS TOTAL: ${totalTime.toFixed(2)}ms`);
      console.log(`?? [CONTEXT-${myId}] FIN LOAD PARAMETRES`);
      console.log(`?? [CONTEXT-${myId}] ======================================`);
    }
  };

  // ============================================================================
  // FONCTIONS UPDATE AVEC DEBOUNCING ET LOGS
  // ============================================================================
  
  const updateParametresGeneraux = async (nouveauxParametres) => {
    const updateId = Math.random().toString(36).substr(2, 6);
    console.log(`?? [UPDATE-GEN-${updateId}] ===== UPDATE PARAMETRES GÉNÉRAUX =====`);
    console.log(`?? [UPDATE-GEN-${updateId}] Données:`, nouveauxParametres);
    
    // ? VÉRIFICATION TOKEN
    if (!checkToken()) {
      console.error(`?? [UPDATE-GEN-${updateId}] Token invalide`);
      setError('Session expirée - Veuillez vous reconnecter');
      return { success: false, error: 'Authentification requise' };
    }
    
    // ? DEBOUNCING
    if (debouncedCallsRef.current.updateGeneraux) {
      console.log(`?? [UPDATE-GEN-${updateId}] Annulation appel précédent (debounce)`);
      clearTimeout(debouncedCallsRef.current.updateGeneraux);
    }
    
    try {
      const response = await api.parametres.updateGeneraux(nouveauxParametres);
      console.log(`?? [UPDATE-GEN-${updateId}] Réponse API:`, response);
      
      if (response.success) {
        console.log(`? [UPDATE-GEN-${updateId}] Success, rechargement programmé...`);
        
        debouncedCallsRef.current.updateGeneraux = setTimeout(async () => {
          console.log(`?? [UPDATE-GEN-${updateId}] Rechargement params après délai`);
          await loadParametres(true, `updateGeneraux-${updateId}`);
        }, 200);
        
        return response;
      }
      return response;
    } catch (error) {
      console.error(`?? [UPDATE-GEN-${updateId}] Erreur:`, error);
      
      // Gestion erreurs d'auth
      if (error.message.includes('Authentification') || error.message.includes('401')) {
        setError('Session expirée - Veuillez vous reconnecter');
        localStorage.removeItem('token');
        handleAuthError();
      }
      
      return { success: false, error: error.message };
    }
  };

  const updateSection = async (sectionId, donneesSection) => {
    const updateId = Math.random().toString(36).substr(2, 6);
    console.log(`?? [UPDATE-SEC-${updateId}] ===== UPDATE SECTION ${sectionId} =====`);
    console.log(`?? [UPDATE-SEC-${updateId}] Données envoyées:`, donneesSection);
    console.log(`?? [UPDATE-SEC-${updateId}] Section actuelle:`, 
      parametres.sections.find(s => s.id == sectionId)
    );
    
    // ? VÉRIFICATION TOKEN
    if (!checkToken()) {
      console.error(`?? [UPDATE-SEC-${updateId}] Token invalide`);
      setError('Session expirée - Veuillez vous reconnecter');
      return { success: false, error: 'Authentification requise' };
    }
    
    // ? DEBOUNCING STRICT
    if (debouncedCallsRef.current.updateSection) {
      console.log(`?? [UPDATE-SEC-${updateId}] Annulation appel précédent (debounce)`);
      clearTimeout(debouncedCallsRef.current.updateSection);
    }
    
    try {
      const response = await api.parametres.updateSection(sectionId, donneesSection);
      console.log(`?? [UPDATE-SEC-${updateId}] Réponse API:`, response);
      
      if (response.success) {
        // On posait ici un `loadParametres(true)` differe de 150 ms : un
        // rechargement de TOUS les parametres — sections, classes, matieres,
        // parametres generaux — pour une seule ligne modifiee. C'est ce qui
        // faisait clignoter l'ecran entier a chaque desactivation de section,
        // et le delai n'etait qu'un pansement sur la course qui s'ensuivait.
        //
        // La route PUT rend deja la ligne a jour. On la pose, et c'est tout.
        const ligne = response.data;
        setParametres(prev => ({
          ...prev,
          sections: (prev.sections || []).map(section =>
            String(section.id) === String(sectionId)
              ? { ...section, ...(ligne || donneesSection) }
              : section
          )
        }));

        return response;
      }
      return response;
    } catch (error) {
      console.error(`?? [UPDATE-SEC-${updateId}] Erreur:`, error);
      
      // Gestion erreurs d'auth
      if (error.message.includes('Authentification') || error.message.includes('401')) {
        setError('Session expirée - Veuillez vous reconnecter');
        localStorage.removeItem('token');
        handleAuthError();
      }
      
      return { success: false, error: error.message };
    }
  };

  const createSection = async (donneesSection) => {
    const createId = Math.random().toString(36).substr(2, 6);
    console.log(`? [CREATE-SEC-${createId}] ===== CREATE SECTION =====`);
    console.log(`? [CREATE-SEC-${createId}] Données:`, donneesSection);
    
    // ? VÉRIFICATION TOKEN
    if (!checkToken()) {
      console.error(`?? [CREATE-SEC-${createId}] Token invalide`);
      setError('Session expirée - Veuillez vous reconnecter');
      return { success: false, message: 'Authentification requise' };
    }
    
    try {
      const response = await api.parametres.createSection(donneesSection);
      console.log(`?? [CREATE-SEC-${createId}] Réponse API:`, response);
      
      if (response.success) {
        // Meme raison que pour la modification : la route rend la ligne creee,
        // on l'ajoute a la liste plutot que de tout relire pour la retrouver.
        const ligne = response.data;
        if (ligne?.id) {
          setParametres(prev => ({
            ...prev,
            sections: [...(prev.sections || []), { active: 1, ...donneesSection, ...ligne }]
              .sort((a, b) => Number(a.ordre || 0) - Number(b.ordre || 0))
          }));
        } else {
          // Une reponse sans identifiant : on ne devine pas, on relit.
          await loadParametres(true, `createSection-${createId}`);
        }
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (err) {
      console.error(`?? [CREATE-SEC-${createId}] Erreur:`, err);
      
      // Gestion erreurs d'auth
      if (err.message.includes('Authentification') || err.message.includes('401')) {
        setError('Session expirée - Veuillez vous reconnecter');
        localStorage.removeItem('token');
        handleAuthError();
      }
      
      return { success: false, message: err.message };
    }
  };

  /**
   * Supprimer une section vide.
   *
   * Le serveur refuse avec un 409 si elle contient des classes — on ne
   * supprime jamais en cascade : effacer une section emporterait ses classes,
   * leurs eleves et leurs notes. Le message dit alors combien de classes la
   * retiennent, et l'ecran peut proposer la desactivation a la place.
   */
  const deleteSection = async (sectionId) => {
    if (!checkToken()) {
      setError('Session expirée - Veuillez vous reconnecter');
      return { success: false, message: 'Authentification requise' };
    }

    try {
      const response = await api.parametres.deleteSection(sectionId);

      if (response.success) {
        setParametres(prev => ({
          ...prev,
          sections: (prev.sections || []).filter(s => String(s.id) !== String(sectionId))
        }));
        return { success: true, message: response.message };
      }
      return { success: false, message: response.message };
    } catch (err) {
      if (err.message.includes('Authentification') || err.message.includes('401')) {
        setError('Session expirée - Veuillez vous reconnecter');
        localStorage.removeItem('token');
        handleAuthError();
      }
      return { success: false, message: err.message };
    }
  };

  const reorderSections = async (nouveauxOrdres) => {
    const reorderId = Math.random().toString(36).substr(2, 6);
    console.log(`?? [REORDER-${reorderId}] ===== REORDER SECTIONS =====`);
    console.log(`?? [REORDER-${reorderId}] Nouveaux ordres:`, nouveauxOrdres);
    
    // ? VÉRIFICATION TOKEN
    if (!checkToken()) {
      console.error(`?? [REORDER-${reorderId}] Token invalide`);
      setError('Session expirée - Veuillez vous reconnecter');
      return { success: false, message: 'Authentification requise' };
    }
    
    try {
      const response = await api.parametres.reorderSections(nouveauxOrdres);
      console.log(`?? [REORDER-${reorderId}] Réponse API:`, response);
      
      if (response.success) {
        // Le glisser-deposer a deja calcule le nouvel ordre et l'a envoye :
        // le relire au serveur ne peut rien apprendre de plus, et rechargeait
        // au passage classes, matieres et parametres generaux.
        const parId = new Map(
          (nouveauxOrdres || []).map(o => [String(o.id), Number(o.ordre)])
        );
        setParametres(prev => ({
          ...prev,
          sections: (prev.sections || [])
            .map(section => parId.has(String(section.id))
              ? { ...section, ordre: parId.get(String(section.id)) }
              : section)
            .sort((a, b) => Number(a.ordre || 0) - Number(b.ordre || 0))
        }));
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (err) {
      console.error(`?? [REORDER-${reorderId}] Erreur:`, err);
      
      // Gestion erreurs d'auth
      if (err.message.includes('Authentification') || err.message.includes('401')) {
        setError('Session expirée - Veuillez vous reconnecter');
        localStorage.removeItem('token');
        handleAuthError();
      }
      
      return { success: false, message: err.message };
    }
  };

  // ============================================================================
  // FONCTIONS UTILITAIRES
  // ============================================================================
  
  const formatPrice = (amount) => {
    const { monnaie_symbole } = parametres.parametres_generaux;
    return new Intl.NumberFormat('fr-FR').format(parseInt(amount || 0)) + ' ' + monnaie_symbole;
  };

  const getSectionById = (sectionId) => {
    return parametres.sections.find(section => section.id === sectionId);
  };

  const getSectionColors = (sectionId) => {
    const section = getSectionById(sectionId);
    if (section?.couleur) {
      const primary = section.couleur;
      return {
        primary,
        secondary: adjustBrightness(primary, 20),
        light: adjustBrightness(primary, 80)
      };
    }
    return { primary: '#6b7280', secondary: '#9ca3af', light: '#f3f4f6' };
  };

  const adjustBrightness = (hex, percent) => {
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return "#" + (0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)).toString(16).slice(1);
  };

  const reloadParametres = async () => {
    const reloadId = Math.random().toString(36).substr(2, 6);
    console.log(`?? [RELOAD-${reloadId}] Reload manuel des paramètres`);
    return await loadParametres(true, `manual-reload-${reloadId}`);
  };

  // ============================================================================
  // EFFET D'INITIALISATION
  // ============================================================================
  
  useEffect(() => {
    console.log('?? [CONTEXT] ===== INITIALISATION PROVIDER =====');
    console.log('?? [CONTEXT] Montage du ParametresProvider');
    
    loadParametres(false, 'initialization');
    
    return () => {
      console.log('?? [CONTEXT] ===== CLEANUP PROVIDER =====');
      console.log('?? [CONTEXT] Démontage du ParametresProvider');
      
      // Cleanup complet
      if (abortRef.current) {
        console.log('?? [CONTEXT] Abort requête en cours');
        abortRef.current.abort();
      }
      
      // Cleanup debounced calls
      Object.keys(debouncedCallsRef.current).forEach(key => {
        if (debouncedCallsRef.current[key]) {
          console.log(`?? [CONTEXT] Clear timeout ${key}`);
          clearTimeout(debouncedCallsRef.current[key]);
        }
      });
      
      isLoadingRef.current = false;
    };
  }, []);

  // ============================================================================
  // DEBUG: LOG des changements d'état
  // ============================================================================
  
  useEffect(() => {
    console.log('?? [CONTEXT] État parametres mis à jour:');
    console.log('?? [CONTEXT] Loading:', loading);
    console.log('?? [CONTEXT] Error:', error);
    console.log('?? [CONTEXT] Sections count:', parametres.sections.length);
    console.log('?? [CONTEXT] Sections détail:', 
      parametres.sections.map(s => ({ id: s.id, nom: s.nom, active: s.active }))
    );
  }, [parametres, loading, error]);

  const value = {
    parametres,
    loading,
    error,
    loadParametres,
    updateParametresGeneraux,
    updateSection,
    createSection,
    deleteSection,
    reorderSections,
    reloadParametres,
    formatPrice,
    getSectionById,
    getSectionColors
  };

  return (
    <ParametresContext.Provider value={value}>
      {children}
    </ParametresContext.Provider>
  );
};